import React, { forwardRef, useMemo, Ref } from 'react';
import { Select } from '../../../ui-library';
import { getDataOrAriaProps } from '../../../utils';
import Error from './Error';

import './styles/Input.css';
import './styles/SelectElement.css';

// --- Types ---

export interface SelectOption {
    label: string;
    value: string | number;
    id?: string | number;
    disabled?: boolean;
}

export interface SelectElementProps {
    options?: SelectOption[] | null;
    onChange?: (value: any) => void;
    required?: boolean;
    value?: any;
    defaultValue?: any;
    placeholder?: string;
    hasSearch?: boolean;
    asyncSearch?: boolean;
    searchInProgress?: boolean;
    loadMoreItems?: () => void;
    hasMoreItems?: boolean;
    disabled?: boolean;
    className?: string;
    label?: string;
    error?: string;
    // Catch-all for data-* or aria-* props
    [key: string]: any;
}

// --- Component ---

const SelectElement = (props: SelectElementProps & { innerRef?: Ref<any> }) => {
    const optionsDefault: SelectOption[] = [{
        label: 'N/A',
        value: 'N/A',
    }];

    const {
        options,
        onChange,
        required,
        value,
        defaultValue,
        placeholder,
        hasSearch,
        asyncSearch,
        searchInProgress,
        loadMoreItems,
        hasMoreItems,
        disabled: disableSelect,
        innerRef,
        className = '',
        label,
        error,
        multiple = false,
        ...rest
    } = props;

    // Memoize the formatted options
    const optionsMemo = useMemo(() => {
        if (options === undefined || options === null) {
            return optionsDefault;
        }

        // Mapping to ensure consistent label/value structure
        const mappedOptions = options.map(({
            id, label, disabled, value: val,
        }) => ({
            label,
            value: val !== undefined ? String(val) : String(id),
            disabled,
        }));

        // Remove duplicates if necessary (using JSON.stringify for object comparison in Set)
        return Array.from(new Set(mappedOptions.map(o => JSON.stringify(o))))
            .map(s => JSON.parse(s));
    }, [options]);

    return (
        <div className={`form-group selectElement inputBlock ${className}`}>
            {label && (
                <label className="inputLabel" htmlFor="inputLabel">
                    {label}
                    {' '}
                    {required && <span> *</span>}
                </label>
            )}
            <div className={`${error ? 'errorBorder' : ''}`}>
                <Select
                    disabled={disableSelect}
                    ref={innerRef}
                    options={optionsMemo}
                    value={defaultValue ?? value}
                    withInfobox={false}
                    onChange={onChange}
                    required={required}
                    placeholder={placeholder}
                    hasSearch={hasSearch}
                    asyncSearch={asyncSearch}
                    searchInProgress={searchInProgress}
                    loadMoreItems={loadMoreItems}
                    hasMoreItems={hasMoreItems}
                    multiple={multiple}
                    {...getDataOrAriaProps(rest)}
                />
            </div>
            {error && <Error>{error}</Error>}
        </div>
    );
};

// --- ForwardRef Export ---

export default forwardRef<any, SelectElementProps>((props, ref) => (
    <SelectElement {...props} innerRef={ref} />
));
